import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import {
  Box,
  Button,
  ButtonGroup,
  Collapse,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addDays, format, startOfDay } from "date-fns";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { DayWithTripIndicator } from "./DatePicker/DayWithTripIndicator";
import { SCHEDULE_MIN_DATE } from "../utils/dateConstants";
import { floorToQuarterHour, snapToQuarterHour } from "../utils/timeUtils";
import { SexyText } from "../widgets/SexyText/SexyText";
import type { GranularityMode } from "../stores/useGranularityMode";

interface TimeRangeSliderProps {
  currentTime: Date;
  rangeStart: Date;
  rangeEnd: Date;
  onTimeChange: (time: Date) => void;
  onRangeChange: (start: Date, end: Date) => void;
  tripCountsByDate?: Map<string, number>;
  // Props for inline date/time controls
  showDateTimePicker?: boolean;
  showNowButton?: boolean;
  // Props for trip duration display
  selectedTripStart?: Date;
  selectedTripEnd?: Date;
  showTripDuration?: boolean;
  selectedTailNumber?: string;
  // Props for jump to trip buttons
  onJumpToNextTrip?: () => void;
  onJumpToPreviousTrip?: () => void;
  showTripJumpButtons?: boolean;
  hasNextTrip?: boolean;
  hasPreviousTrip?: boolean;
  // Props for View Schedule button
  onViewSchedule?: () => void;
  showViewScheduleButton?: boolean;
  viewScheduleDisabled?: boolean;
  // Compact mode (for backward compatibility with tests)
  compact?: boolean;
  // Granularity mode props
  granularityMode?: GranularityMode;
  onGranularityChange?: (mode: GranularityMode) => void;
  showGranularityToggle?: boolean;
}

export const TimeRangeSlider: React.FC<TimeRangeSliderProps> = ({
  currentTime,
  rangeStart,
  rangeEnd,
  onTimeChange,
  onRangeChange,
  tripCountsByDate,
  showDateTimePicker = false,
  showNowButton = false,
  selectedTripStart,
  selectedTripEnd,
  showTripDuration = false,
  selectedTailNumber,
  onJumpToNextTrip,
  onJumpToPreviousTrip,
  showTripJumpButtons = false,
  hasNextTrip = false,
  hasPreviousTrip = false,
  onViewSchedule,
  showViewScheduleButton = false,
  viewScheduleDisabled = false,
  compact = false,
  granularityMode = "15m",
  onGranularityChange,
  showGranularityToggle = false,
}) => {
  const { t } = useTranslation();
  // State to track which range button is selected
  const [selectedRange, setSelectedRange] = React.useState<"1W" | "2W" | "1M">(
    "1W"
  );

  // Convert time to slider value (0-100 representing position within range)
  const timeToSliderValue = (time: Date): number => {
    const totalRange = rangeEnd.getTime() - rangeStart.getTime();
    const currentPosition = time.getTime() - rangeStart.getTime();
    return Math.max(0, Math.min(100, (currentPosition / totalRange) * 100));
  };

  // Convert slider value back to time
  const sliderValueToTime = (value: number): Date => {
    const totalRange = rangeEnd.getTime() - rangeStart.getTime();
    const timeOffset = (value / 100) * totalRange;
    return new Date(rangeStart.getTime() + timeOffset);
  };

  const currentSliderValue = timeToSliderValue(currentTime);

  // Counter for reliable flashing on range changes
  const [rangeChangeCounter, setRangeChangeCounter] = React.useState(0);

  React.useEffect(() => {
    setRangeChangeCounter((prev) => prev + 1);
  }, [rangeStart, rangeEnd]);

  // Calculate trip duration bar position and width
  const tripDurationBar = React.useMemo(() => {
    if (!showTripDuration || !selectedTripStart || !selectedTripEnd) {
      return null;
    }

    const startValue = timeToSliderValue(selectedTripStart);
    const endValue = timeToSliderValue(selectedTripEnd);

    // Calculate visible portion of the trip
    const visibleStartValue = Math.max(0, Math.min(100, startValue));
    const visibleEndValue = Math.max(0, Math.min(100, endValue));
    const visibleWidth = visibleEndValue - visibleStartValue;

    // Only show if trip has visible portion
    if (visibleWidth <= 0) {
      return null;
    }

    const result = {
      left: visibleStartValue,
      width: visibleWidth,
    };

    return result;
  }, [
    showTripDuration,
    selectedTripStart,
    selectedTripEnd,
    rangeStart,
    rangeEnd,
  ]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    if (typeof value === "number") {
      const newTime = sliderValueToTime(value);
      // Snap to 15-minute intervals when clicking/dragging
      const snappedTime = new Date(newTime);
      const minutes = Math.round(snappedTime.getMinutes() / 15) * 15;
      snappedTime.setMinutes(minutes, 0, 0);
      onTimeChange(snappedTime);
    }
  };

  // Handle keyboard navigation (arrow keys for 15-minute increments)
  const handleKeyDown = (event: React.KeyboardEvent) => {
    const fifteenMinutes = 15 * 60 * 1000;
    let newTime: Date | null = null;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        newTime = new Date(currentTime.getTime() - fifteenMinutes);
        break;
      case "ArrowRight":
        event.preventDefault();
        newTime = new Date(currentTime.getTime() + fifteenMinutes);
        break;
      case "Home":
        event.preventDefault();
        newTime = new Date(rangeStart);
        break;
      case "End":
        event.preventDefault();
        newTime = new Date(rangeEnd);
        break;
    }

    if (newTime && newTime >= rangeStart && newTime <= rangeEnd) {
      onTimeChange(newTime);
    }
  };

  // Range control functions
  const setWeekRange = () => {
    setSelectedRange("1W");
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 3);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setDate(end.getDate() + 4);
    end.setHours(23, 59, 59, 999);

    onRangeChange(start, end);
  };

  const setTwoWeekRange = () => {
    setSelectedRange("2W");
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setDate(end.getDate() + 7);
    end.setHours(23, 59, 59, 999);

    onRangeChange(start, end);
  };

  const setMonthRange = () => {
    setSelectedRange("1M");
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 15);
    start.setHours(0, 0, 0, 0);

    const end = new Date(now);
    end.setDate(end.getDate() + 15);
    end.setHours(23, 59, 59, 999);

    onRangeChange(start, end);
  };

  // Jump to now function for inline Now button
  const jumpToNow = () => {
    const now = new Date();
    onTimeChange(floorToQuarterHour(now));
  };

  const shiftTime = (minutes: number) => {
    const time = minutes * 60 * 1000;
    const newTime = new Date(currentTime.getTime() + time);
    onTimeChange(newTime);
  };

  // Daily mode navigation functions
  const goToPreviousDay = () => {
    const newDate = addDays(startOfDay(currentTime), -1);
    onTimeChange(newDate);
  };

  const goToNextDay = () => {
    const newDate = addDays(startOfDay(currentTime), 1);
    onTimeChange(newDate);
  };

  const handleGranularityToggle = (
    _: React.MouseEvent<HTMLElement>,
    newMode: GranularityMode | null
  ) => {
    if (newMode && onGranularityChange) {
      onGranularityChange(newMode);
    }
  };

  // Derived state
  const isDailyMode = granularityMode === "daily";

  // Smooth visibility styles - collapses width when hidden to prevent overflow
  const getVisibilityStyles = (visible: boolean, direction: "left" | "right" = "right") => ({
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? "auto" as const : "none" as const,
    transform: visible ? "translateX(0)" : `translateX(${direction === "left" ? "-" : ""}12px)`,
    width: visible ? "auto" : 0,
    overflow: "hidden" as const,
    transition: "opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  });

  // Main layout - vertical stacking with controls on top, slider on bottom
  return (
    <Stack
      direction="column"
      spacing={0}
      sx={{
        width: "100%",
      }}
    >
      {/* Controls row */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="flex-end"
        sx={{
          "& > *": {
            transition: "all 0.2s ease-in-out",
          },
        }}
      >
        {/* Granularity toggle */}
        {showGranularityToggle && (
          <ToggleButtonGroup
            value={granularityMode}
            exclusive
            onChange={handleGranularityToggle}
            size="small"
            sx={{
              height: "27px",
              "& .MuiToggleButton-root": {
                fontSize: "0.75rem",
                px: 1.5,
                py: 0,
                height: "27px",
                textTransform: "none",
              },
            }}
          >
            <ToggleButton value="daily">
              {t("components.timeControl.daily", "Daily")}
            </ToggleButton>
            <ToggleButton value="15m">
              {t("components.timeControl.15m", "15m")}
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Range controls - only shown in 15m mode */}
        <Box sx={getVisibilityStyles(!isDailyMode, "left")}>
          <ButtonGroup
            size="small"
            variant="outlined"
            sx={{
              height: "27px",
              "& .MuiButton-root": {
                height: "27px",
                minWidth: "40px",
                fontSize: "0.75rem",
                px: 1,
              },
            }}
          >
            <Button
              onClick={setWeekRange}
              variant={selectedRange === "1W" ? "contained" : "outlined"}
            >
              1W
            </Button>
            <Button
              onClick={setTwoWeekRange}
              variant={selectedRange === "2W" ? "contained" : "outlined"}
            >
              2W
            </Button>
            <Button
              onClick={setMonthRange}
              variant={selectedRange === "1M" ? "contained" : "outlined"}
            >
              1M
            </Button>
          </ButtonGroup>
        </Box>

        {/* Daily mode: DatePicker (consistent styling with 15m DateTimePicker) */}
        <Box sx={{ ...getVisibilityStyles(isDailyMode && !!showDateTimePicker), display: "flex", alignItems: "center", height: "27px" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={currentTime}
              onChange={(newDate) => {
                if (newDate) {
                  onTimeChange(startOfDay(newDate));
                }
              }}
              minDate={SCHEDULE_MIN_DATE}
              slots={{
                day: DayWithTripIndicator as any,
              }}
              slotProps={{
                day: {
                  tripCountsByDate,
                } as any,
                textField: {
                  size: "small",
                  sx: {
                    width: 140,
                    height: "27px !important",
                    minHeight: "unset !important",
                    boxSizing: "border-box",
                    "& .MuiPickersTextField-root": {
                      height: "27px !important",
                    },
                    "& .MuiInputBase-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                      fontSize: "0.75rem",
                      paddingTop: "0 !important",
                      paddingBottom: "0 !important",
                    },
                    "& .MuiPickersInputBase-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                    },
                    "& .MuiPickersOutlinedInput-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "3px 8px !important",
                      lineHeight: "1.2 !important",
                      height: "auto !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      top: "0 !important",
                    },
                    "& .MuiInputAdornment-root": {
                      height: "27px !important",
                      "& .MuiIconButton-root": {
                        padding: "2px !important",
                        "& .MuiSvgIcon-root": {
                          fontSize: "1rem !important",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Daily mode: Navigation buttons in ButtonGroup (matches 15m navigation style) */}
        <Box sx={getVisibilityStyles(isDailyMode && !!showNowButton)}>
          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              height: "27px",
              "& .MuiButton-root": {
                height: "27px",
              },
            }}
          >
            <Tooltip title={t("components.timeControl.previousDay", "Previous day")} arrow>
              <Button
                onClick={goToPreviousDay}
                sx={{
                  fontSize: "0.75rem",
                  px: 1,
                  minWidth: "45px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                -1d
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              onClick={() => onTimeChange(startOfDay(new Date()))}
              disabled={
                startOfDay(currentTime).getTime() === startOfDay(new Date()).getTime()
              }
              sx={{
                fontSize: "0.75rem",
                px: 1.5,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}
            >
              {t("components.timeControl.today", "Today")}
            </Button>
            <Tooltip title={t("components.timeControl.nextDay", "Next day")} arrow>
              <Button
                onClick={goToNextDay}
                sx={{
                  fontSize: "0.75rem",
                  px: 1,
                  minWidth: "45px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                +1d
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>

        {/* 15m mode: DateTimePicker */}
        <Box sx={{ ...getVisibilityStyles(!isDailyMode && !!showDateTimePicker, "left"), display: "flex", alignItems: "center", height: "27px" }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              value={currentTime}
              onChange={(newDate) => {
                if (newDate) {
                  onTimeChange(snapToQuarterHour(newDate));
                }
              }}
              minDate={SCHEDULE_MIN_DATE}
              ampm={false}
              minutesStep={15}
              slots={{
                day: DayWithTripIndicator as any,
              }}
              slotProps={{
                day: {
                  tripCountsByDate,
                } as any,
                textField: {
                  size: "small",
                  sx: {
                    width: 210,
                    height: "27px !important",
                    minHeight: "unset !important",
                    boxSizing: "border-box",
                    "& .MuiPickersTextField-root": {
                      height: "27px !important",
                    },
                    "& .MuiInputBase-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                      fontSize: "0.75rem",
                      paddingTop: "0 !important",
                      paddingBottom: "0 !important",
                    },
                    "& .MuiPickersInputBase-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                    },
                    "& .MuiPickersOutlinedInput-root": {
                      height: "27px !important",
                      minHeight: "unset !important",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "0.75rem",
                      padding: "3px 8px !important",
                      lineHeight: "1.2 !important",
                      height: "auto !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      top: "0 !important",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.75rem",
                      transform: "translate(8px, 6px) scale(1)",
                      "&.Mui-focused, &.MuiFormLabel-filled": {
                        transform: "translate(8px, -8px) scale(0.75)",
                      },
                    },
                    "& .MuiInputAdornment-root": {
                      height: "27px !important",
                      "& .MuiIconButton-root": {
                        padding: "2px !important",
                        "& .MuiSvgIcon-root": {
                          fontSize: "1rem !important",
                        },
                      },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Time navigation buttons group - only shown in 15m mode */}
        <Box sx={getVisibilityStyles(!isDailyMode && (!!showNowButton || !!showTripJumpButtons), "left")}>
          <ButtonGroup
            variant="outlined"
            size="small"
            sx={{
              height: "27px",
              "& .MuiButton-root": {
                height: "27px",
              },
            }}
          >
            {/* Previous trip button */}
            {showTripJumpButtons && (
              <Tooltip
                title={
                  !hasPreviousTrip
                    ? t('components.timeControl.noPreviousTrips')
                    : t('components.timeControl.jumpToPreviousTrip')
                }
                arrow
              >
                <span>
                  <Button
                    onClick={onJumpToPreviousTrip}
                    disabled={!hasPreviousTrip || !onJumpToPreviousTrip}
                    startIcon={<FlightLandIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      fontSize: "0.75rem",
                      px: { xs: 0.75, sm: 1.25 },
                      minWidth: { xs: 40, sm: "auto" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& .MuiButton-startIcon": {
                        mr: { xs: 0, sm: 0.5 },
                        ml: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      {t('components.timeControl.prevTrip')}
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            )}

            {/* Time shift buttons */}
            {showNowButton && (
              <>
                <Button
                  onClick={() => shiftTime(-60 * 24 * 7)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  -1w
                </Button>
                <Button
                  onClick={() => shiftTime(-15)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  -15m
                </Button>
                <Button
                  disabled={
                    currentTime.getTime() ===
                    floorToQuarterHour(new Date()).getTime()
                  }
                  variant="contained"
                  onClick={jumpToNow}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1.5,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {t('components.timeControl.now')}
                </Button>
                <Button
                  onClick={() => shiftTime(15)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  +15m
                </Button>
                <Button
                  onClick={() => shiftTime(60 * 24 * 7)}
                  sx={{
                    fontSize: "0.75rem",
                    px: 1,
                    minWidth: "45px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  +1w
                </Button>
              </>
            )}

            {/* Next trip button */}
            {showTripJumpButtons && (
              <Tooltip
                title={
                  !hasNextTrip
                    ? t('components.timeControl.noFutureTrips')
                    : t('components.timeControl.jumpToNextTrip')
                }
                arrow
              >
                <span>
                  <Button
                    onClick={onJumpToNextTrip}
                    disabled={!hasNextTrip || !onJumpToNextTrip}
                    endIcon={<FlightTakeoffIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      fontSize: "0.75rem",
                      px: { xs: 0.75, sm: 1.25 },
                      minWidth: { xs: 40, sm: "auto" },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "& .MuiButton-endIcon": {
                        ml: { xs: 0, sm: 0.5 },
                        mr: 0,
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ display: { xs: "none", sm: "inline" } }}
                    >
                      {t('components.timeControl.nextTrip')}
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            )}
          </ButtonGroup>
        </Box>

        {/* View Schedule button */}
        {showViewScheduleButton && (
          <Button
            variant="outlined"
            startIcon={<CalendarViewMonthIcon />}
            onClick={onViewSchedule}
            disabled={viewScheduleDisabled}
            size="small"
            sx={{
              fontSize: "0.75rem",
              px: 1.5,
            }}
          >
            {t('components.timeControl.viewSchedule')}
          </Button>
        )}
      </Stack>

      {/* Slider row - only shown in 15m mode with collapse animation */}
      <Collapse in={!isDailyMode} timeout={300} easing="cubic-bezier(0.4, 0, 0.2, 1)">
        <Box
          sx={{
            width: "100%",
            pt: 1.25, // Add padding-top to make room for trip duration bar (~10px)
            overflow: "visible", // Allow bar to show above
            position: "relative",
          }}
        >
        {/* Trip duration bar - positioned within padding area */}
        {tripDurationBar &&
          (() => {
            return (
              <>
                <Box
                  sx={{
                    position: "absolute",
                    top: 15,
                    left: `calc(${tripDurationBar.left}%)`,
                    width: `${tripDurationBar.width}%`,
                    height: 6,
                    backgroundColor: "success.main",
                    zIndex: 0,
                  }}
                  title={t('components.timeControl.tripDurationTitle')}
                />
              </>
            );
          })()}
        <Slider
          value={currentSliderValue}
          onChange={handleSliderChange}
          onKeyDown={handleKeyDown}
          min={0}
          max={100}
          step={0.1}
          marks={(() => {
            const marks = [];
            const rangeInDays = (rangeEnd.getTime() - rangeStart.getTime()) / (24 * 60 * 60 * 1000);

            // Determine step size based on range
            let dayStep = 1;
            if (rangeInDays > 14) {
              dayStep = Math.ceil(rangeInDays / 7); // Show ~7 markers for longer ranges
            } else if (rangeInDays > 7) {
              dayStep = 2; // Every other day for 2 week range
            }

            // Always include the start
            marks.push({
              value: 0,
              label: (
                <SexyText
                  value={rangeChangeCounter}
                  text={rangeStart.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                  bidirectional={true}
                />
              ),
            });

            // Add day boundary markers
            const currentDate = new Date(rangeStart);
            currentDate.setHours(0, 0, 0, 0);
            currentDate.setDate(currentDate.getDate() + dayStep);

            while (currentDate < rangeEnd) {
              const sliderValue = timeToSliderValue(currentDate);
              // Only add if not too close to start or end
              if (sliderValue > 5 && sliderValue < 95) {
                marks.push({
                  value: sliderValue,
                  label: (
                    <SexyText
                      value={rangeChangeCounter}
                      text={currentDate.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                      bidirectional={true}
                    />
                  ),
                });
              }
              currentDate.setDate(currentDate.getDate() + dayStep);
            }

            // Always include the end
            marks.push({
              value: 100,
              label: (
                <SexyText
                  value={rangeChangeCounter}
                  text={rangeEnd.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                  bidirectional={true}
                />
              ),
            });

            return marks;
          })()}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => {
            const date = sliderValueToTime(value);
            return format(date, "MMM d, HH:mm");
          }}
          getAriaValueText={(value) => {
            const date = sliderValueToTime(value);
            return format(date, "MMM d, HH:mm");
          }}
          aria-label={t('components.timeControl.timeSelectionAriaLabel')}
          sx={{
            height: 6,
            "& .MuiSlider-track": {
              backgroundColor: "primary.main",
              border: "none",
            },
            "& .MuiSlider-rail": {
              backgroundColor: "primary.main",
              borderRadius: 0,
              opacity: 0.38,
            },
            "& .MuiSlider-mark": {
              backgroundColor: "text.primary",
              height: 8,
              width: 2,
              opacity: 1,
            },
            "& .MuiSlider-markLabel": {
              fontSize: "0.7rem",
              transform: "translateX(-50%)",
              top: 24,
              color: "text.secondary",
            },
            "& .MuiSlider-thumb": {
              width: 20,
              height: 20,
              backgroundColor: "primary.main",
              border: "3px solid",
              borderColor: "background.paper",
              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
              cursor: "grab",
              zIndex: 1,
              "&:hover": {
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              },
              "&:focus, &.Mui-focusVisible": {
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              },
              "&.Mui-active": {
                cursor: "grabbing !important",
                boxShadow: "0 3px 8px rgba(0,0,0,0.3) !important",
              },
            },
            ".MuiSlider-rail": {
              opacity: 1,
            },
          }}
        />
        </Box>
      </Collapse>
    </Stack>
  );
};
